import React from 'react'
import { Trans } from '@lingui/react/macro'
import { useLingui } from '@lingui/react/macro'
import { XStack, YStack } from 'tamagui'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'

const Page = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          <Trans>
            Every Event has its own page where attendees can learn about the
            gathering, see who is going, and RSVP or purchase tickets.
          </Trans>
        </Paragraph>
        <DocScreenshot
          src='docs/events/event-page.png'
          alt={t`Event page for Code Buddy Club showing cover photo, event title, host Guild Toronto JavaScript, date and time, Online label, attendee avatars with count, Attend button, and description`}
          caption={t`An Event page with cover photo, host Guild, date and time, attendees, and Attend button`}
        />
      </Card>

      <Card gap='$4'>
        <DocSection id='page-sections' title={t`Page Sections`}>
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='image'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Cover photo
                  </Paragraph>{' '}
                  — Displayed as a banner at the top of the page, followed by
                  the Event title. Organizers also see{' '}
                  <Paragraph size='$5' fontWeight='$7'>
                    Copy
                  </Paragraph>{' '}
                  and{' '}
                  <Paragraph size='$5' fontWeight='$7'>
                    Settings
                  </Paragraph>{' '}
                  buttons for quick access to Event management.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='people-community'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Host Guild
                  </Paragraph>{' '}
                  — Shown below the title with its photo and name, linking back
                  to the Guild profile. The date and time appear with a calendar
                  icon, and the Event type is indicated — for example, an{' '}
                  <Paragraph size='$5' fontWeight='$7'>
                    Online
                  </Paragraph>{' '}
                  label for virtual Events or a venue address displayed as a
                  clickable map link for in-person Events.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='people'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Attendees
                  </Paragraph>{' '}
                  — Profile photos of people who have RSVPed along with the
                  total count, giving visitors a quick sense of who will be
                  there and how popular the Event is. Depending on whether the
                  Event is free or ticketed, visitors see an{' '}
                  <Paragraph size='$5' fontWeight='$7'>
                    Attend
                  </Paragraph>{' '}
                  button to RSVP or a{' '}
                  <Paragraph size='$5' fontWeight='$7'>
                    Checkout
                  </Paragraph>{' '}
                  button to purchase tickets.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='edit'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Description
                  </Paragraph>{' '}
                  — Supports rich text formatting, allowing organizers to
                  include detailed information about the Event agenda, what
                  attendees should bring, and any other relevant details.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='projection-screen'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Presentations
                  </Paragraph>{' '}
                  — If{' '}
                  <Link href='/docs/presentations/overview'>Presentations</Link>{' '}
                  have been added to the schedule, they appear in their own
                  section so attendees can see who is speaking and what topics
                  are covered.
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='event-visibility' title={t`Visibility`}>
          <Paragraph size='$5'>
            <Trans>
              Events can be{' '}
              <Link href='/docs/events/visibility'>listed or unlisted</Link>.
              Listed Events appear in your Guild&apos;s public Events tab and in
              search results. Unlisted Events are only accessible via direct
              link.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>
    </>
  )
}

export default Page
